import React, { useEffect, useState } from "react";
import { Tabs, TabsList, TabsTrigger } from "./ui/tabs";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Card } from "./ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "./ui/table";
import { Clock, ArrowRight, Flame } from "lucide-react";
import { useI18n } from "../i18n";
import { getRoutes } from "../lib/api";
import { useBooking } from "../lib/booking-context";
import { useCurrency } from "../lib/currency-context";

const catLabel = (t, c) => ({ city: t("routes.city"), airport: t("routes.airport"), long_distance: t("routes.long") }[c] || c);

export const PopularRoutes = () => {
  const { t } = useI18n();
  const { requestBooking } = useBooking();
  const { fmt } = useCurrency();
  const [filter, setFilter] = useState("all");
  const [routes, setRoutes] = useState([]);

  useEffect(() => {
    let active = true;
    getRoutes("all").then((d) => active && setRoutes(d.routes || [])).catch(() => {});
    return () => { active = false; };
  }, []);

  const filtered = filter === "all" ? routes : routes.filter((r) => r.category === filter);

  const book = (r) => requestBooking({
    from_location: r.from, to_location: r.to, category: r.category,
    route: `${r.from} → ${r.to}`, estimated_price: r.price_czk, distance_km: r.distance_km,
  });

  return (
    <section id="routes" className="py-14 sm:py-18 lg:py-24" data-testid="popular-routes-section">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
          <div className="max-w-2xl">
            <h2 className="font-display text-2xl sm:text-3xl font-semibold tracking-[-0.015em]">{t("routes.title")}</h2>
            <p className="mt-3 text-muted-foreground">{t("routes.subtitle")}</p>
          </div>
          <Tabs value={filter} onValueChange={setFilter}>
            <TabsList className="rounded-xl bg-secondary p-1 h-auto flex-wrap">
              {["all", "city", "airport", "long_distance"].map((c) => (
                <TabsTrigger key={c} value={c} data-testid={`routes-filter-${c}`} className="rounded-lg data-[state=active]:bg-primary data-[state=active]:text-primary-foreground text-xs sm:text-sm px-3 py-1.5">
                  {c === "all" ? t("routes.all") : catLabel(t, c)}
                </TabsTrigger>
              ))}
            </TabsList>
          </Tabs>
        </div>

        {/* Desktop table */}
        <div className="mt-8 hidden md:block rounded-2xl border border-border overflow-hidden">
          <Table data-testid="popular-routes-table">
            <TableHeader>
              <TableRow className="bg-secondary/60 hover:bg-secondary/60">
                <TableHead className="font-semibold text-foreground">{t("routes.col.route")}</TableHead>
                <TableHead className="font-semibold text-foreground">{t("routes.col.distance")}</TableHead>
                <TableHead className="font-semibold text-foreground">{t("routes.col.time")}</TableHead>
                <TableHead className="font-semibold text-foreground">{t("routes.col.price")}</TableHead>
                <TableHead className="text-right"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map((r) => (
                <TableRow key={r.id} data-testid={`route-row-${r.id}`}>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <span className="font-medium">{r.from} <ArrowRight className="inline h-3.5 w-3.5 text-muted-foreground" /> {r.to}</span>
                      {r.popular && <Badge className="bg-accent text-accent-foreground hover:bg-accent rounded-full text-[10px] px-2"><Flame className="h-3 w-3 mr-0.5" />{t("routes.popular")}</Badge>}
                    </div>
                    <span className="text-xs text-muted-foreground">{catLabel(t, r.category)}</span>
                  </TableCell>
                  <TableCell className="tabular-nums">{r.distance_km} km</TableCell>
                  <TableCell className="tabular-nums text-muted-foreground"><Clock className="inline h-3.5 w-3.5 mr-1" />{r.duration_min} min</TableCell>
                  <TableCell><span className="text-xs text-muted-foreground mr-1">{t("routes.from")}</span><span className="font-display font-semibold text-lg tabular-nums">{fmt(r.price_czk)}</span></TableCell>
                  <TableCell className="text-right">
                    <Button size="sm" variant="secondary" className="rounded-lg border border-border" onClick={() => book(r)} data-testid={`route-book-${r.id}`}>{t("routes.book")}</Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>

        {/* Mobile cards */}
        <div className="mt-6 grid grid-cols-1 gap-3 md:hidden">
          {filtered.map((r) => (
            <Card key={r.id} className="p-4 rounded-2xl" data-testid={`route-card-${r.id}`}>
              <div className="flex items-center justify-between">
                <span className="text-xs text-muted-foreground">{catLabel(t, r.category)}</span>
                {r.popular && <Badge className="bg-accent text-accent-foreground hover:bg-accent rounded-full text-[10px]">{t("routes.popular")}</Badge>}
              </div>
              <p className="mt-1 font-medium">{r.from} <ArrowRight className="inline h-3.5 w-3.5" /> {r.to}</p>
              <div className="mt-2 flex items-center gap-4 text-sm text-muted-foreground tabular-nums">
                <span>{r.distance_km} km</span><span><Clock className="inline h-3.5 w-3.5 mr-1" />{r.duration_min} min</span>
              </div>
              <div className="mt-3 flex items-center justify-between">
                <span className="font-display font-semibold text-xl tabular-nums">{fmt(r.price_czk)}</span>
                <Button size="sm" className="rounded-lg" onClick={() => book(r)} data-testid={`route-book-m-${r.id}`}>{t("routes.book")}</Button>
              </div>
            </Card>
          ))}
        </div>
      </div>
    </section>
  );
};
